import type {
  FabricPath,
  ID,
  Material,
  MatrixInfo,
  Shape,
  Style3dOptions,
  Style3dSDKInstance,
  UVInfo
} from './typing.ts';
import { reactive, ref } from 'vue';
import { getMMNum } from '@repo/utils';
import { getFabricPath, getPieceShapeMap } from './utils.ts';
import { omit } from 'lodash-es';
import { handleUvAndMatrix, releaseUvInfo } from './matrix.ts';
import { toReactive } from '@vueuse/core';
import { Viewer as GlbViewer } from '@repo/glb';

declare global {
  interface Window {
    Style3D: any;
  }
}

// 0:Current, 1:Front, 2:Left, 3:Right, 4:Back, 5:Custom
type Angle = 0 | 1 | 2 | 3 | 4 | 5;

let style3dSDKInstance: Style3dSDKInstance;

let baseUrl = '';

const style3dSDK = new Proxy({}, {
  get(_target, prop) {
    if (!style3dSDKInstance) {
      throw new Error('Style3dSDK is not initialized. Please initialize it before using.');
    }
    return (style3dSDKInstance as any)[prop];
  },
  set(_target, prop, value) {
    if (!style3dSDKInstance) {
      throw new Error('Style3dSDK is not initialized. Please initialize it before using.');
    }
    (style3dSDKInstance as any)[prop] = value;
    return true;
  }
}) as Style3dSDKInstance;

function setTransprarencyRenderEnabled(transprarency: boolean = true) {
  if (style3dSDK?.lincpro?.setTransprarencyRenderEnabled) {
    style3dSDK?.lincpro?.setTransprarencyRenderEnabled(transprarency);
  }
}

async function setMaterialInfoOffset(shapes: Shape[], _materialList: Material[]) {
  if (!shapes || shapes.length == 0) {
    return;
  }

  let copiedUvInfo: UVInfo | null;
  let copiedMatrixInfo: MatrixInfo | null;
  copiedUvInfo = await style3dSDK.getClothesUvInfo();
  copiedMatrixInfo = await style3dSDK.getClothesGeometryFabricMatrix();

  let newMatrixInfo: MatrixInfo | null;
  newMatrixInfo = handleUvAndMatrix(shapes, _materialList, copiedUvInfo, copiedMatrixInfo);
  // 使用完数据后，解除引用
  releaseUvInfo(copiedUvInfo);
  copiedUvInfo = null;
  copiedMatrixInfo = null;

  //更新id
  await style3dSDK.setClothesGeometryFabricMatrix(newMatrixInfo!);
  newMatrixInfo = null;

  for (let i = 0; i < shapes.length; i++) {
    const item = shapes[i]!;
    if (item.pos && (item.pos.x || item.pos.y) && item.dpm && item.width && item.height) {
      // 偏移不为空 进行偏移ou
      const materials = _materialList.filter((v) => v.name === item.piece);
      if (!materials || materials.length === 0) {
      } else {
        // 选中
        await style3dSDK!.selectMaterial(materials[0]!.id, -1, false, 0);
        if (item.pos.x && item.dpm) {
          // x偏移
          const width = getMMNum(item.dpm.x, item.width);
          let x = item.pos.x;
          if (item.offset && item.clipOffset) {
            x = x - item.offset.x * 10 + item.clipOffset?.left;
          }
          item.pos.x = width ? Number(((x % width) / width).toFixed(13)) : 0;

          await style3dSDK!.setFabricOffsetX(item.pos.x);
        }
        if (item.pos.y && item.dpm) {
          // x偏移
          const height = getMMNum(item.dpm.y, item.height);
          let y = item.pos.y;
          y = height ? Number(((height - (y % height)) / height).toFixed(13)) : 0;
          await style3dSDK!.setFabricOffsetY(y);
        }
      }
    }
  }
}

const instanceWrapper = reactive({
  instance: null as (ReturnType<typeof createStyle3dSDKInstance> | null)
});

function createSDKProxy(style3dOptions: Partial<Style3dOptions>) {
  const sdkCache: { [key: string]: any } = {};
  let model: 'GLB' | 'SCO' | '' = '';

  return <Style3dSDKInstance>new Proxy(
    {},
    {
      set(_target, prop, value) {
        // 通过 `model` 动态切换 SDK
        console.log(`Setting value '${String(value)}' to '${String(prop)}'`);
        if (prop === 'model') {
          model = value;
          if (!sdkCache[value]) {
            if (value === 'GLB') {
              const dom = document.getElementById('glb-con');
              // @ts-ignore-next-line
              sdkCache[value] = new GlbViewer({
                  dom,
                  ...style3dOptions
                }
              );
            } else if (value === 'SCO') {
              const dom = document.getElementById('style3d-con');
              sdkCache[value] = new window.Style3D.Viewer({
                dom,
                ...style3dOptions
              });
            } else {
              console.log('Invalid model');
            }
            return true;
          }

          return true;
        }
        return false;
      },

      get(_target, prop) {
        const currentSDK = sdkCache[model];
        if (currentSDK && typeof currentSDK[prop] === 'function') {
          return currentSDK[prop].bind(currentSDK);
        }

        if (currentSDK && prop in currentSDK) {
          return currentSDK[prop];
        }

        console.log(`Warning: The method '${String(prop)}' does not exist in the current SDK.`);
        return () => {
        };
      }
    }
  );
}

function createStyle3dSDKInstance() {
  const loading = ref(false);
  const isReady = ref(false);
  const model = ref<'GLB' | 'SCO'>('SCO');

  let funcSelectNothingMaterial: (() => void) | undefined = undefined;

  async function initStyle3d(ops: Style3dOptions) {
    const options = omit(ops, 'baseUrl');

    const style3dOptions = {
      displayLevel: options.displayLevel,
      custom: {
        backgroundStyle: {
          color: options.backgroundColor
        }
      },
      events: {
        onReady: () => {
          isReady.value = true;
        },
        ...options.events
      },
      config: {
        disableGeoBackPart: true
      }
    };

    style3dSDKInstance = createSDKProxy(style3dOptions);

    if (style3dOptions.events){
      funcSelectNothingMaterial = style3dOptions.events.onNothingSelected;
    }

    setTransprarencyRenderEnabled();
    await style3dSDK.setImageProcessStrategy(0);
  }

  async function loadSco(scoSrc: string) {
    isReady.value = false;
    await style3dSDK.load(scoSrc, null, null, null);
  }

  interface FabricLoadOption {
    fabricRootPath?: string;
    fabric?: {
      type: string,
      id: string,
      originUrl?: string
    };
    color?: string;
    resolution?: 'sd' | 'hd';
    regionColors?:string[];
    colorChangePromise?: (fabricRootPath: string, regionColors: string[]) => Promise<string | undefined>;
  }

  async function loadFabric(shape: Shape[], option: FabricLoadOption, 
    specified :{id:ID}|{materialIndex:number}|{getShapeOnly:boolean}|null = null) {
    if (!shape || shape.length === 0) {
      return [];
    }

    const { fabricRootPath = '', resolution = 'sd', fabric, regionColors } = option;
    baseUrl = fabricRootPath;

    await style3dSDK.setRenderEnabled(false);
    const map = getPieceShapeMap(shape);
    const materialList = await style3dSDK.getMaterialList();

    let specifiedMaterialIndex:number|undefined = undefined;
    if (specified != null ){
      if ('id' in specified) {
        const find :(Shape|undefined)= materialList.find(({ id }) => id === specified.id);
        if (find){
          const mapvalue = map.get(find.name);
          if (mapvalue){
            specifiedMaterialIndex = mapvalue.materialIndex;
          }
        }
      }
      else if ('materialIndex' in specified){
        specifiedMaterialIndex = specified.materialIndex;
      }
    }

    const shapes = materialList
      .filter(({ name, id }) => {
        return map.has(`${id}`) || map.has(name);
      })
      .map(item => ({
        ...item,
        ...(map.has(`${item.id}`) ? map.get(`${item.id}`) : map.get(item.name)),
        ...(fabric ? { fabric } : {}),
        id: item.id
      }))
      .filter((shape) => shape !== undefined)
      .filter((item) => {
        if (specifiedMaterialIndex !== undefined){
          return item.materialIndex === specifiedMaterialIndex;
        }
        return true;
      })
      .map(item => ({ ...item, name: item.onlyColor ? '' : item.name }));

    if (specified && ('getShapeOnly' in specified) && specified.getShapeOnly
      || shapes.length <= 0){ //添加此判断，否则sco的情况面料位置会有偏移
      await style3dSDK.setRenderEnabled(true);
    } else { 
      const materialIds: ID[] = [];
      const fabricsInfoList: FabricPath[] = [];
      const colors: string[] = [];
      
      // 如果有区域颜色，需要先进行区域变色处理（只处理一次）
      const hasRegionColors = regionColors && regionColors.length > 0;
      let regionColoredImageUrl: string | undefined = undefined;
      
      if (hasRegionColors && fabricRootPath && option.colorChangePromise) {
        try {
          console.log('[loadFabric] 开始区域变色处理:', { fabricRootPath, regionColors });
          
          // 添加超时保护
          const timeoutPromise = new Promise<never>((_, reject) => {
            setTimeout(() => reject(new Error('区域变色超时（30秒）')), 30000);
          });
          
          regionColoredImageUrl = await Promise.race([
            option.colorChangePromise(fabricRootPath, regionColors),
            timeoutPromise
          ]);
        } catch (error) {
          console.error('[loadFabric] 区域变色失败:', error);
          // 失败时继续执行，不阻塞加载流程
        }
      }
      
      for (const shape of shapes) {
        let fabricPath = getFabricPath(shape, baseUrl, resolution);
        
        // 如果有区域变色的图片，使用它
        if (regionColoredImageUrl) {
          if (shape.type === 'main'){
            fabricPath = {
              ...fabricPath,
              xhrPath: regionColoredImageUrl,
              path: regionColoredImageUrl
            };
          }
          colors.push('');
        }
        else{
          const c = (shape?.fabric && option?.color) ? option.color : shape.color ? shape.color : '';
          colors.push(c);
        }
        materialIds.push(shape.id);
        fabricsInfoList.push(fabricPath);
      }
      await style3dSDK.changeFabricsByMaterials(materialIds, fabricsInfoList, colors);

      if (specifiedMaterialIndex !== undefined){
        shape = shape.filter(item => item.materialIndex === specifiedMaterialIndex);
      }

      await setMaterialInfoOffset(shape, materialList);
      await style3dSDK.setRenderEnabled(true);
      await style3dSDK.clearMaterialSelection();
      await style3dSDK.clearHistorySteps();
    }

    return shapes as Shape[];
  }

  async function resetCameraViewState() {
    // 连续调用两次可以避免CameraViewState不能正确归位的问题
    await style3dSDK.setCameraViewState(0);
    await style3dSDK.setCameraViewState(0);
  }

  async function changeFabricColor(color: string) {
    return await style3dSDK.changeFabricColor(color);
  }

  async function getScreenshot(angle: Angle = 1) {
    return await style3dSDK.getScreenshot(angle, false, false);
  }

  async function selectMaterial(id: ID, highlightDelayMillisecond = -1, autoLocateEnabled = false, flashTimes = 0) {
    await style3dSDK.selectMaterial(id, highlightDelayMillisecond, autoLocateEnabled, flashTimes);
  }

  async function clearMaterialSelection() {
    await style3dSDK.clearMaterialSelection();
    if (funcSelectNothingMaterial) {
      funcSelectNothingMaterial();
    }
  }

  async function changeFabric(fabricInfo: FabricPath) {
    await style3dSDK.changeFabricsByMaterials([fabricInfo.id], [fabricInfo]);
  }

  async function setFabricRotation(angle: number) {
    return await style3dSDK.setFabricRotation(angle);
  }

  async function setFabricCycle(cycle: number) {
    return await style3dSDK.setFabricCycle(cycle);
  }
  
  async function setFabricOffsetX(offsetX: number) {
    return await style3dSDK.setFabricOffsetX(offsetX);
  }
  
  async function setFabricOffsetY(offsetY: number) {
    return await style3dSDK.setFabricOffsetY(offsetY);
  }

  async function getMaterialList() {
    return await style3dSDK.getMaterialList();
  }

  async function getFabricState() {
    return await style3dSDK.getFabricState();
  }

  async function setRenderEnabled(enabled: boolean) {
    await style3dSDK.setRenderEnabled(enabled);
  }

  function getStyle3dSDKInstance() {
    if (!style3dSDKInstance) {
      throw new Error('Style3dSDK is not initialized. Please initialize it before using.');
    }
    return style3dSDKInstance;
  }

  function setModel(m: 'GLB' | 'SCO') {
    model.value = m;
    // @ts-ignore-next-line 通过 Proxy 设置 model, 忽略警告
    style3dSDKInstance.model = m;
  }

  function centerizeOnResize(){
    style3dSDKInstance.lincpro?.resize();
  }

  return {
    model,
    setModel,
    style3dSDK,
    isReady,
    initStyle3d,
    loading,
    loadSco,
    loadFabric,
    resetCameraViewState,
    centerizeOnResize,
    getScreenshot,
    selectMaterial,
    clearMaterialSelection,
    changeFabric,
    setFabricRotation,
    setFabricCycle,
    setFabricOffsetX,
    setFabricOffsetY,
    changeFabricColor,
    getMaterialList,
    getFabricState,
    setRenderEnabled,
    getStyle3dSDKInstance
  };
}

export function useStyle3d() {
  if (!instanceWrapper.instance) {
    instanceWrapper.instance = toReactive(createStyle3dSDKInstance());
  }
  return instanceWrapper.instance;
}
